import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Undo2, HandCoins, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  (n === null || n === undefined) ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const METHOD_LABEL = {
  transfer_reversal: "transfer reversal (pulls the fee back from the merchant's Stripe balance)",
  account_debit: "account debit (transfers the fee from the merchant's Stripe balance to your platform)",
};

const ZeroFeeTransactionsModal = ({ open, onOpenChange, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [confirmTx, setConfirmTx] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const getAuth = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const load = useCallback(async (d = days) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/platform-fee/zero-fee-transactions?days=${d}`, getAuth());
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load zero-fee payments");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const recover = async (tx) => {
    setBusyId(tx.transaction_id);
    try {
      const res = await axios.post(
        `${API}/admin/platform-fee/zero-fee-transactions/${tx.transaction_id}/recover`,
        { confirm: true }, getAuth()
      );
      toast.success(`Recovered ${fmtMoney(res.data.recovered_fee)} (${res.data.method === "account_debit" ? "account debit" : "transfer reversal"})`);
      setConfirmTx(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed", { duration: 8000 });
      setConfirmTx(null);
    } finally {
      setBusyId(null);
    }
  };

  const waive = async (tx, undo = false) => {
    setBusyId(tx.transaction_id);
    try {
      await axios.post(
        `${API}/admin/platform-fee/zero-fee-transactions/${tx.transaction_id}/waive`,
        { undo }, getAuth()
      );
      toast.success(undo ? "Waive undone — payment counts toward the warning again" : "Waived — this payment no longer counts toward the warning");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const txns = data?.transactions || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="zero-fee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Payments collected without a platform fee
            </DialogTitle>
            <DialogDescription>
              These payments went through before the fee system took effect (or under a 0% override).
              You can collect the missed fee retroactively — real money moves from the merchant's Stripe balance — or waive it to clear the warning.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1">
              {[30, 90, 365].map((d) => (
                <button
                  key={d}
                  onClick={() => { setDays(d); }}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${days === d ? "border-[#FFCC00]/50 text-[#FFCC00] bg-[#FFCC00]/5" : "border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid={`zero-fee-days-${d}`}
                >
                  Last {d} days
                </button>
              ))}
            </div>
            {data && (
              <p className="text-xs text-muted-foreground" data-testid="zero-fee-summary">
                {data.open_count} open · recoverable <span className="font-mono font-semibold text-[#FFCC00]">{fmtMoney(data.recoverable_total)}</span>
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : txns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="zero-fee-empty">
              No zero-fee payments in this window. 🎉
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr className="text-left text-muted-foreground">
                    <th className="font-medium px-3 py-2">Date</th>
                    <th className="font-medium px-3 py-2">Merchant</th>
                    <th className="font-medium px-3 py-2">Invoice / Customer</th>
                    <th className="font-medium px-3 py-2 text-right">Amount</th>
                    <th className="font-medium px-3 py-2 text-right">Missed fee</th>
                    <th className="font-medium px-3 py-2">Why</th>
                    <th className="font-medium px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.transaction_id} className={`border-t border-border/40 ${t.status === "waived" ? "opacity-50" : ""}`} data-testid={`zero-fee-row-${t.transaction_id}`}>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(t.paid_at)}</td>
                      <td className="px-3 py-2">
                        <span className="text-foreground">{t.merchant_business || t.merchant_email || "—"}</span>
                        <span className={`block text-[9px] uppercase tracking-wider ${t.charge_type === "direct" ? "text-emerald-500/70" : "text-amber-500/70"}`}>
                          {t.charge_type === "direct" ? "direct" : "legacy"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {t.invoice_number || "—"}{t.customer_name ? ` · ${t.customer_name}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-foreground">{fmtMoney(t.amount)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-[#FFCC00]">
                        {fmtMoney(t.expected_fee)}
                        <span className="block text-[9px] font-sans font-normal text-muted-foreground">
                          {t.expected_fee_percent}% + {fmtMoney((t.expected_fee_flat_cents || 0) / 100)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                        {t.zero_fee_reason === "zero_override" ? "0% override" : "pre-fee system"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {t.status === "waived" ? (
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" disabled={busyId === t.transaction_id} onClick={() => waive(t, true)} data-testid={`zero-fee-unwaive-${t.transaction_id}`}>
                            <Undo2 className="w-3 h-3" /> Undo waive
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {t.recovery_method !== "unavailable" && t.zero_fee_reason !== "zero_override" && (
                              <Button size="sm" className="h-7 text-xs gap-1" disabled={busyId === t.transaction_id} onClick={() => setConfirmTx(t)} data-testid={`zero-fee-recover-${t.transaction_id}`}>
                                {busyId === t.transaction_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <HandCoins className="w-3 h-3" />} Collect {fmtMoney(t.expected_fee)}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={busyId === t.transaction_id} onClick={() => waive(t)} data-testid={`zero-fee-waive-${t.transaction_id}`}>
                              <EyeOff className="w-3 h-3" /> Waive
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">
            Collect uses a Stripe {`transfer reversal (legacy charges) or account debit (direct charges)`}. It can fail if the merchant's Stripe balance is insufficient — retry after their next payout cycle. Waived payments stay visible here and can be undone.
          </p>
        </DialogContent>
      </Dialog>

      {/* Recovery confirmation */}
      <AlertDialog open={!!confirmTx} onOpenChange={(o) => { if (!o) setConfirmTx(null); }}>
        <AlertDialogContent data-testid="zero-fee-recover-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Collect {fmtMoney(confirmTx?.expected_fee)} from {confirmTx?.merchant_business || confirmTx?.merchant_email}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This is <strong>real money movement</strong>: a {METHOD_LABEL[confirmTx?.recovery_method] || "Stripe recovery"}.
                </p>
                <p>
                  Payment: {fmtMoney(confirmTx?.amount)}{confirmTx?.invoice_number ? ` (${confirmTx.invoice_number})` : ""} on {fmtDate(confirmTx?.paid_at)} — missed fee computed at {confirmTx?.expected_fee_percent}% + {fmtMoney((confirmTx?.expected_fee_flat_cents || 0) / 100)}.
                </p>
                <p className="text-amber-500">The merchant will see this deduction in their Stripe balance. This cannot be undone from here.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="zero-fee-recover-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); recover(confirmTx); }} disabled={busyId === confirmTx?.transaction_id} data-testid="zero-fee-recover-confirm-btn">
              {busyId === confirmTx?.transaction_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Collect fee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ZeroFeeTransactionsModal;
